import React from 'react';
import { useIntl } from 'react-intl';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { pctSymbol, currencySymbol } from '../../../utils/formatters';

const ValuationChatWidget = ({ widget }) => {
  console.log('ValuationChatWidget component called with:', widget);
  
  if (!widget) {
    console.warn('ValuationChatWidget: No widget prop provided');
    return null;
  }

  const renderWidget = () => {
    switch (widget.type) {
      case 'confidence_comparison':
        return <ConfidenceComparisonWidget data={widget.data} />;
      case 'growth_analysis':
        return <GrowthAnalysisWidget data={widget.data} />;
      case 'ebitda_optimization':
        return <EbitdaOptimizationWidget data={widget.data} />;
      case 'data_completeness':
        return <DataCompletenessWidget data={widget.data} />;
      case 'data_update':
        return <DataUpdateWidget data={widget.data} />;
      case 'valuation_summary':
        return <ValuationSummaryWidget data={widget.data} />;
      case 'valuation_scenario':
        return <ValuationScenarioWidget data={widget.data} />;
      default:
        return <GenericWidget widget={widget} />;
    }
  };

  return (
    <div className="bg-succession-bg-secondary border border-succession-border-subtle rounded-md p-2 mt-1 text-xs">
      {renderWidget()}
    </div>
  );
};

const ConfidenceComparisonWidget = ({ data }) => {
  const intl = useIntl();
  const { before, after, current, potential, improvement, missing_items } = data;
  
  const beforeScore = before || current;
  const afterScore = after || potential;
  const scoreDiff = afterScore ? (afterScore.score - beforeScore.score) : (improvement || 0);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-succession-success';
    if (score >= 60) return 'text-succession-warning';
    return 'text-succession-error';
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <ChartBarIcon className="h-3 w-3 text-succession-primary" />
        <span className="text-succession-text-primary font-medium">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.confidence' })}:</span>
        <span className={`font-semibold ${getScoreColor(beforeScore.score)}`}>
          {beforeScore.score}{pctSymbol()}
        </span>
        <ArrowUpIcon className="h-3 w-3 text-succession-success" />
        <span className={`font-semibold ${getScoreColor(afterScore.score)}`}>
          {afterScore.score}{pctSymbol()}
        </span>
      </div>

      <div className="text-succession-success font-medium">
        +{scoreDiff}{pctSymbol()}
      </div>
    </div>
  );
};

const GrowthAnalysisWidget = ({ data }) => {
  const { revenue_history, growth_rate, trend, confidence_impact, message, impact, years_needed } = data;

  // Handle case where it's just an impact message
  if (message) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ArrowTrendingUpIcon className="h-3 w-3 text-succession-primary" />
          <span className="text-succession-text-primary font-medium">{message}</span>
        </div>
        <div className="text-succession-success font-medium">{impact}</div>
      </div>
    );
  }

  // Handle actual revenue history data
  if (revenue_history && Object.keys(revenue_history).length > 0) {
    const years = Object.keys(revenue_history).sort();
    const revenues = years.map(year => revenue_history[year]);

    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ArrowTrendingUpIcon className="h-3 w-3 text-succession-primary" />
          <span className="text-succession-text-primary font-medium">{years.length} years of data</span>
        </div>
        {growth_rate && (
          <div className={`font-medium ${growth_rate > 0 ? 'text-succession-success' : 'text-succession-error'}`}>
            {(growth_rate * 100).toFixed(1)}{pctSymbol()}/yr
          </div>
        )}
      </div>
    );
  }

  return null;
};

const EbitdaOptimizationWidget = ({ data }) => {
  const intl = useIntl();
  const { current_ebitda, margin_percentage, industry_benchmark, potential_addbacks, common_addbacks } = data;

  return (
    <div>
      <div className="flex items-center mb-2">
        <CurrencyDollarIcon className="h-4 w-4 text-succession-primary mr-2" />
        <h4 className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.ebitda_enhancement' })}</h4>
      </div>

      {current_ebitda !== undefined && (
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="text-center">
            <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.current_ebitda' })}</div>
            <div className="text-sm font-bold text-succession-text-primary">
              ${current_ebitda.toLocaleString()}
            </div>
            <div className={`text-xs ${
              margin_percentage < 15 ? 'text-succession-error' : 
              margin_percentage < 25 ? 'text-succession-warning' : 'text-succession-success'
            }`}>
              {intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.margin_suffix' }, { name: `${margin_percentage}%` })}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.industry_target' })}</div>
            <div className="text-sm font-bold text-succession-success">
              {industry_benchmark}{pctSymbol()}
            </div>
            <div className="text-xs text-succession-text-muted">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.advisory_practices' })}</div>
          </div>
        </div>
      )}

      {(common_addbacks || potential_addbacks) && (
        <div className="space-y-2">
          <div className="text-xs text-succession-text-secondary font-medium">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.common_addbacks' })}</div>
          {(common_addbacks || potential_addbacks).map((addback, index) => (
            <div key={index} className="flex items-start space-x-2">
              <div className="w-1.5 h-1.5 bg-succession-primary rounded-full mt-1.5"></div>
              <div className="flex-1">
                <div className="text-xs text-succession-text-primary">
                  {addback.category || addback}
                </div>
                {addback.description && (
                  <div className="text-xs text-succession-text-muted">
                    {addback.description}
                  </div>
                )}
                {addback.typical_amount && (
                  <div className="text-xs text-succession-success">
                    Typically: {addback.typical_amount}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DataCompletenessWidget = ({ data }) => {
  const intl = useIntl();
  const { completeness_percentage, missing_fields, completed_fields } = data;
  
  const getCompletenessColor = (percentage) => {
    if (percentage >= 80) return 'text-succession-success';
    if (percentage >= 60) return 'text-succession-warning';
    return 'text-succession-error';
  };

  return (
    <div>
      <div className="flex items-center mb-2">
        <InformationCircleIcon className="h-4 w-4 text-succession-primary mr-2" />
        <h4 className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.data_completeness' })}</h4>
      </div>
      
      <div className="text-center mb-3">
        <div className={`text-2xl font-bold ${getCompletenessColor(completeness_percentage)}`}>
          {completeness_percentage}{pctSymbol()}
        </div>
        <div className="text-xs text-succession-text-secondary">
          {completed_fields} of {completed_fields + missing_fields.length} fields complete
        </div>
      </div>

      {missing_fields && missing_fields.length > 0 && (
        <div>
          <div className="text-xs text-succession-text-secondary mb-2">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.still_needed' })}</div>
          <div className="space-y-1">
            {missing_fields.slice(0, 4).map((field, index) => (
              <div key={index} className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-3 w-3 text-succession-error" />
                <span className="text-xs text-succession-text-primary">
                  {field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DataUpdateWidget = ({ data }) => {
  const intl = useIntl();
  const { updated_fields, new_confidence } = data;

  return (
    <div>
      <div className="flex items-center mb-2">
        <CheckCircleIcon className="h-4 w-4 text-succession-success mr-2" />
        <h4 className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.data_updated' })}</h4>
      </div>
      
      <div className="space-y-2">
        <div className="text-center">
          <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.updated_fields' })}</div>
          <div className="flex flex-wrap gap-1 justify-center">
            {updated_fields.map((field, index) => (
              <span key={index} className="px-2 py-1 bg-succession-success-10 text-succession-success text-xs rounded">
                {field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            ))}
          </div>
        </div>

        {new_confidence && (
          <div className="text-center pt-2 border-t border-succession-border-subtle">
            <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.new_confidence' })}</div>
            <div className="text-lg font-bold text-succession-success">
              {new_confidence}{pctSymbol()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ValuationSummaryWidget = ({ data }) => {
  const intl = useIntl();
  if (!data) {
    console.error('ValuationSummaryWidget: No data provided');
    return null;
  }

  console.log('ValuationSummaryWidget received data:', data);
  console.log('ValuationSummaryWidget data structure:', JSON.stringify(data, null, 2));

  // Handle nested data structure - backend sometimes sends { title: "...", data: {...} }
  const actualData = data.data || data;
  console.log('ValuationSummaryWidget actual data:', actualData);

  // Handle both naming conventions: backend uses "value" and "revenue",
  // but we also support "current_value" and "annual_revenue"
  const {
    value,
    current_value,
    revenue,
    annual_revenue,
    ebit,
    growth_rate,
    confidence_score,
    percentile
  } = actualData;

  // Use whichever field is available
  const valuationValue = current_value || value;
  const revenueValue = annual_revenue || revenue;
  const ebitValue = ebit;

  // Parse growth_rate if it's a string like "-0.9%"
  const parseGrowthRate = (rate) => {
    if (!rate) return null;
    if (typeof rate === 'number') return rate;
    // Remove % and convert to decimal
    const numRate = parseFloat(rate.replace('%', ''));
    return numRate / 100;
  };

  const growthRateValue = parseGrowthRate(growth_rate);

  const formatValue = (value) => {
    const sym = currencySymbol();
    if (!value) return `${sym}0`;

    // If already formatted as string (e.g., "$-2,034,026" or "£-2,034,026"), return as-is
    if (typeof value === 'string' && (value.includes('$') || value.includes('\u00a3'))) {
      return value;
    }

    // Convert string to number if needed (remove currency symbols and commas)
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[$£,]/g, '')) : value;
    if (isNaN(numValue) || numValue === 0) return `${sym}0`;
    if (numValue >= 1000000) return `${sym}${(numValue / 1000000).toFixed(1)}M`;
    if (numValue >= 1000) return `${sym}${(numValue / 1000).toFixed(0)}K`;
    if (numValue < 0) return `-${sym}${Math.abs(numValue).toLocaleString()}`;
    return `${sym}${numValue.toLocaleString()}`;
  };

  const getConfidenceColor = (score) => {
    if (score >= 80) return 'text-succession-success';
    if (score >= 60) return 'text-succession-warning';
    return 'text-succession-error';
  };

  return (
    <div>
      <div className="flex items-center mb-2">
        <ChartBarIcon className="h-4 w-4 text-succession-primary mr-2" />
        <h4 className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.valuation_summary' })}</h4>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-center">
          <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.current_value' })}</div>
          <div className="text-lg font-bold text-succession-text-primary">
            {formatValue(valuationValue)}
          </div>
        </div>

        <div className="text-center">
          <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.annual_revenue' })}</div>
          <div className="text-lg font-bold text-succession-text-primary">
            {formatValue(revenueValue)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {confidence_score && (
            <div className="text-center">
              <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.confidence' })}</div>
              <div className={`text-sm font-semibold ${getConfidenceColor(confidence_score)}`}>
                {confidence_score}{pctSymbol()}
              </div>
            </div>
          )}

          {growthRateValue && growthRateValue !== 0 && (
            <div className="text-center">
              <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.growth' })}</div>
              <div className={`text-sm font-semibold ${growthRateValue > 0 ? 'text-succession-success' : 'text-succession-error'}`}>
                {(growthRateValue * 100).toFixed(1)}{pctSymbol()}
              </div>
            </div>
          )}
        </div>

        {percentile && percentile > 0 && (
          <div className="text-right">
            <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.industry_position' })}</div>
            <div className="text-sm font-semibold text-succession-primary">
              {percentile}th {intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.percentile' })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ValuationScenarioWidget = ({ data }) => {
  const intl = useIntl();
  const { 
    scenario_name, 
    current_value, 
    scenario_value,
    value_change,
    value_change_percentage,
    year_1_valuation,
    year_3_valuation, 
    year_5_valuation, 
    annual_revenue,
    ebitda,
    ebitda_margin,
    percentile,
    insights,
    key_takeaway,
    parameters_modified, 
    created_at, 
    valuation_breakdown, 
    scenario_id 
  } = data;

  const formatValue = (value) => {
    const sym = currencySymbol();
    if (!value || value === 0) return `${sym}0`;
    if (value >= 1000000) return `${sym}${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${sym}${(value / 1000).toFixed(0)}K`;
    return `${sym}${value.toLocaleString()}`;
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-succession-success';
    if (change < 0) return 'text-succession-error';
    return 'text-succession-text-secondary';
  };

  return (
    <div>
      <div className="flex items-center mb-3">
        <ArrowTrendingUpIcon className="h-4 w-4 text-succession-primary mr-2" />
        <h4 className="text-sm font-medium text-succession-text-primary">{scenario_name}</h4>
        {created_at && (
          <span className="text-xs text-succession-text-secondary ml-auto">{created_at}</span>
        )}
      </div>

      {/* Current vs Scenario Comparison */}
      <div className="bg-succession-bg-primary rounded-lg p-3 mb-3 border border-succession-border-subtle">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-succession-text-secondary font-medium">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.valuation_impact' })}</span>
          <span className={`text-xs font-bold ${getChangeColor(value_change_percentage)}`}>
            {value_change_percentage > 0 ? '+' : ''}{value_change_percentage?.toFixed(1)}{pctSymbol()}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.current' })}</div>
            <div className="text-lg font-bold text-succession-text-primary">
              {valuation_breakdown?.current || formatValue(current_value)}
            </div>
          </div>
          <div>
            <div className="text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.scenario' })}</div>
            <div className="text-lg font-bold text-succession-primary">
              {formatValue(scenario_value || year_1_valuation)}
            </div>
          </div>
        </div>
        {value_change && (
          <div className={`text-sm font-medium mt-2 ${getChangeColor(value_change)}`}>
            {value_change > 0 ? '+' : ''}{formatValue(value_change)} {intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.change' })}
          </div>
        )}
      </div>

      {/* Key Metrics */}
      {(annual_revenue || ebitda) && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {annual_revenue && (
            <div className="bg-succession-bg-tertiary rounded p-2">
              <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.revenue' })}</div>
              <div className="text-sm font-bold text-succession-text-primary">
                {formatValue(annual_revenue)}
              </div>
            </div>
          )}
          {ebitda && (
            <div className="bg-succession-bg-tertiary rounded p-2">
              <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.ebitda' })}</div>
              <div className="text-sm font-bold text-succession-text-primary">
                {formatValue(ebitda)}
                {ebitda_margin && (
                  <span className="text-xs text-succession-text-muted ml-1">
                    ({ebitda_margin.toFixed(1)}%)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 5-Year Projection */}
      <div className="mb-3">
        <div className="text-xs text-succession-text-secondary font-medium mb-2">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.five_year_projection' })}</div>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center">
            <div className="text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.year_1' })}</div>
            <div className="text-sm font-bold text-succession-text-primary">
              {valuation_breakdown?.year_1 || formatValue(year_1_valuation)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.year_3' })}</div>
            <div className="text-sm font-bold text-succession-text-primary">
              {valuation_breakdown?.year_3 || formatValue(year_3_valuation)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-succession-text-muted mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.year_5' })}</div>
            <div className="text-sm font-bold text-succession-text-primary">
              {valuation_breakdown?.year_5 || formatValue(year_5_valuation)}
            </div>
          </div>
        </div>
      </div>

      {/* LLM Insights */}
      {(insights || key_takeaway) && (
        <div className="bg-succession-bg-tertiary rounded-lg p-3 mb-3">
          {insights && (
            <p className="text-xs text-succession-text-primary mb-2">{insights}</p>
          )}
          {key_takeaway && (
            <p className="text-xs font-medium text-succession-primary">
              💡 {key_takeaway}
            </p>
          )}
        </div>
      )}

      {/* Parameters Modified */}
      {parameters_modified && Object.keys(parameters_modified).length > 0 && (
        <div className="border-t border-succession-border-subtle pt-2 mb-3">
          <div className="text-xs text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.parameters_modified' })}</div>
          <div className="space-y-1">
            {Object.entries(parameters_modified).map(([key, value]) => (
              <div key={key} className="flex justify-between text-xs">
                <span className="text-succession-text-primary">
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                </span>
                <span className="text-succession-success font-medium">
                  {typeof value === 'number' ? formatValue(value) : value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Complete Scenario Button */}
      {scenario_id && (
        <div className="border-t border-succession-border-subtle pt-2">
          <a
            href={`/succession/valuation?dcf_id=${scenario_id}`}
            className="inline-flex items-center justify-center w-full px-3 py-2 bg-succession-primary text-white text-sm font-medium rounded-md hover:bg-succession-primary-dark transition-colors duration-200"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ArrowTrendingUpIcon className="h-4 w-4 mr-2" />
            {intl.formatMessage({ id: 'succession.agent_system.valuation_chat_widget.view_complete_valuation' })}
          </a>
        </div>
      )}
    </div>
  );
};

const GenericWidget = ({ widget }) => {
  return (
    <div>
      <div className="flex items-center mb-2">
        <InformationCircleIcon className="h-4 w-4 text-succession-primary mr-2" />
        <h4 className="text-sm font-medium text-succession-text-primary">{widget.title}</h4>
      </div>
      <div className="text-xs text-succession-text-secondary">
        {JSON.stringify(widget.data, null, 2)}
      </div>
    </div>
  );
};

export default ValuationChatWidget;